import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';

import { IState } from '../../reducers';
import { ILanguageReducer } from '../../reducers/languageReducer';
import { colorStack } from '../../styleHelpers/colors';
import { languageList } from '../../tools/languageTools';
import { fontSizeAndHeight } from '../../styleHelpers/fontSizes';

const RELOAD_DELAY = 30;

const formatRemainingTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

type LangCode = 'en' | 'fr' | 'pl' | 'de' | 'es' | 'it' | 'nl' | 'pt' | 'ru' | 'uk';

const messages: Record<LangCode, (time: string) => string> = {
    en: time => `A new version is available. Reloading in ${time}…`,
    fr: time => `Nouvelle version disponible. Rechargement dans ${time}…`,
    pl: time => `Nowa wersja aplikacji. Przeładowanie za ${time}…`,
    de: time => `Neue Version verfügbar. Neuladen in ${time}…`,
    es: time => `Nueva versión disponible. Recargando en ${time}…`,
    it: time => `Nuova versione disponibile. Ricaricamento in ${time}…`,
    nl: time => `Nieuwe versie beschikbaar. Herladen over ${time}…`,
    pt: time => `Nova versão disponível. Recarregando em ${time}…`,
    ru: time => `Доступна новая версия. Перезагрузка через ${time}…`,
    uk: time => `Нова версія доступна. Перезавантаження через ${time}…`,
};

const reloadNowLabel: Record<LangCode, string> = {
    en: 'Reload now',
    fr: 'Recharger maintenant',
    pl: 'Odśwież teraz',
    de: 'Jetzt neu laden',
    es: 'Recargar ahora',
    it: 'Ricarica ora',
    nl: 'Nu herladen',
    pt: 'Recarregar agora',
    ru: 'Обновить сейчас',
    uk: 'Оновити зараз',
};

const getLangCode = (lcid: number): LangCode => {
    const code = languageList[lcid as keyof typeof languageList] as LangCode | undefined;
    return (code && code in messages) ? code : 'en';
};

const slideIn = keyframes`
    from { transform: translateY(-100%); opacity: 0; }
    to   { transform: translateY(0);     opacity: 1; }
`;

const countdownProgress = keyframes`
    from { width: 100%; }
    to   { width: 0%; }
`;

const Banner = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 99999;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 12px 24px;
    background: ${colorStack.middleBlue};
    color: ${colorStack.white};
    font-size: ${fontSizeAndHeight[14]};
    font-family: inherit;
    box-shadow: 0 2px 8px ${colorStack.black}33;
    animation: ${slideIn} 0.3s ease;
`;

const ReloadButton = styled.button`
    background: ${colorStack.white};
    color: ${colorStack.middleBlue};
    border: none;
    border-radius: 4px;
    padding: .5rem 1rem;
    font-size: ${fontSizeAndHeight[13]};
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    &:hover { background: ${colorStack.whiteBlue}; }
`;

const ProgressBar = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    height: 2px;
    background: ${colorStack.white};
    width: 100%;
    animation: ${countdownProgress} ${RELOAD_DELAY}s linear forwards;
`;

export const UpdateNotificationBanner: FC = () => {
    const { userLanguage } = useSelector<IState, ILanguageReducer>(state => state.language);
    const [seconds, setSeconds] = useState(RELOAD_DELAY);
    const lang = getLangCode(userLanguage);
    const remainingTime = formatRemainingTime(seconds);

    useEffect(() => {
        if (seconds <= 0) {
            window.location.reload();
            return;
        }
        const timer = setTimeout(() => setSeconds(s => s - 1), 1000);
        return () => clearTimeout(timer);
    }, [seconds]);

    return (
        <Banner role="alert">
            <span>{messages[lang](remainingTime)}</span>
            <ReloadButton onClick={() => window.location.reload()}>
                {reloadNowLabel[lang]}
            </ReloadButton>
            <ProgressBar />
        </Banner>
    );
};
